import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';

type Schwere = 'success' | 'error' | 'info' | 'warning';

type FeedbackContextType = {
  zeigeErfolg: (nachricht: string) => void;
  zeigeFehler: (nachricht: string) => void;
  zeigeHinweis: (nachricht: string) => void;
};

const FeedbackContext = createContext<FeedbackContextType>({
  zeigeErfolg: () => {},
  zeigeFehler: () => {},
  zeigeHinweis: () => {}
});

/**
 * Einheitliche Rückmeldungen für die ganze Anwendung.
 *
 * Vorher hatte jede Seite ihre eigene Snackbar- oder Alert-Logik, teils mit
 * `alert()`. Damit sahen Erfolgs- und Fehlermeldungen je Seite anders aus.
 */
export const FeedbackProvider = ({ children }: { children: ReactNode }) => {
  const [offen, setOffen] = useState(false);
  const [nachricht, setNachricht] = useState('');
  const [schwere, setSchwere] = useState<Schwere>('success');

  const zeige = useCallback((text: string, art: Schwere) => {
    setNachricht(text);
    setSchwere(art);
    setOffen(true);
  }, []);

  const wert = useMemo(
    () => ({
      zeigeErfolg: (text: string) => zeige(text, 'success'),
      zeigeFehler: (text: string) => zeige(text, 'error'),
      zeigeHinweis: (text: string) => zeige(text, 'info')
    }),
    [zeige]
  );

  return (
    <FeedbackContext.Provider value={wert}>
      {children}
      <Snackbar
        open={offen}
        autoHideDuration={schwere === 'error' ? 8000 : 4000}
        onClose={() => setOffen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOffen(false)}
          severity={schwere}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {nachricht}
        </Alert>
      </Snackbar>
    </FeedbackContext.Provider>
  );
};

export const useFeedback = () => useContext(FeedbackContext);
